import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";

/**
 * Small ⓘ icon with hover tooltip. Touch-friendly (tap to toggle).
 * The popup is rendered into document.body via a portal so it escapes any
 * ancestor with `overflow: hidden` or `overflow: auto` (e.g. tables with
 * horizontal scroll, rounded cards). Position is computed from the trigger's
 * bounding rect and updated using fixed positioning.
 *
 * Usage: <InfoTooltip tip="Loan balance ÷ current value" />
 */
export function InfoTooltip({ tip }: { tip: string }) {
  const [visible, setVisible] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!visible || !triggerRef.current) {
      setPos(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({ top: rect.top, left: rect.left + rect.width / 2 });

    const hide = () => setVisible(false);
    // Capture phase so we catch scroll events on any ancestor.
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [visible]);

  return (
    <span
      ref={triggerRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onClick={(e) => {
        e.stopPropagation();
        setVisible((v) => !v);
      }}
    >
      <Info size={13} className="text-neutral-400 cursor-help ml-1 flex-shrink-0" />
      {visible && pos &&
        createPortal(
          <span
            className="fixed z-[9999] w-64 -translate-x-1/2 -translate-y-full -mt-2 rounded-lg bg-neutral-900 px-3 py-2 text-xs text-white shadow-lg leading-relaxed pointer-events-none whitespace-normal text-left"
            style={{ top: pos.top, left: pos.left }}
          >
            {tip}
            {/* Arrow — sits just below the tooltip body pointing at the icon */}
            <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-neutral-900" />
          </span>,
          document.body,
        )}
    </span>
  );
}
